import React, { FormEventHandler, useState } from 'react'
import styled from 'styled-components/macro'
import { toast } from 'react-toastify'
import log from 'loglevel'
import { RpcError } from 'grpc-web'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { ListControlsRequest } from '@trustero/trustero-api-web/lib/model/control_pb'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { useControls } from 'src/components/async/model/control/useControls'
import { useSaveFrameworkSections } from 'src/pages/Frameworks/frameworks.hooks'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import type { RequirementEditSelection } from 'src/pages/Frameworks/show/frameworksShowSections.types'
import { ModalForm, ModalFormId } from '../..'

const WarningList = styled.ul`
  margin: 12px 0 0;
  padding-left: 20px;
`

type Props = {
  framework: ComplianceFramework
  selectedRequirement: RequirementEditSelection | null
  clearSelectedRequirement: () => void
}

export const DeleteFrameworkRequirementConfirmModal = ({
  framework,
  selectedRequirement,
  clearSelectedRequirement,
}: Props): JSX.Element => {
  const show = useIsShowModal(ModalFormId.DELETE_FRAMEWORK_REQUIREMENT_CONFIRM)
  const hideModal = useHideModal({
    modalId: ModalFormId.DELETE_FRAMEWORK_REQUIREMENT_CONFIRM,
  })
  const saveSections = useSaveFrameworkSections()
  const cfModelId = framework.getModelId()
  const { data: controlsData, isLoading: controlsLoading } = useControls(
    new ListControlsRequest(),
    show,
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  const closeAndReset = () => {
    hideModal()
    setIsSubmitting(false)
    clearSelectedRequirement()
  }

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault()
    if (!selectedRequirement) return
    setIsSubmitting(true)
    try {
      const newSections = selectedRequirement.sectionDraftsSnapshot.map((s) =>
        s.tempId !== selectedRequirement.sectionId
          ? s
          : {
              ...s,
              requirements: s.requirements.filter(
                (r) => r.tempId !== selectedRequirement.requirementTempId,
              ),
            },
      )
      await saveSections({
        complianceFrameworkModelId: cfModelId,
        originalSections: selectedRequirement.sectionDraftsSnapshot,
        newSections,
        controls: controlsData?.getItemsList() ?? [],
      })
      toast.success('Requirement deleted.')
      closeAndReset()
    } catch (err) {
      const errorMessage = getErrorMessage(err as RpcError)
      toast.error(errorMessage.message)
      if (errorMessage.shouldLog) {
        log.error('DeleteFrameworkRequirementConfirmModal submit failed', err)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ModalForm
      show={show}
      hide={closeAndReset}
      formId={ModalFormId.DELETE_FRAMEWORK_REQUIREMENT_CONFIRM}
      size="lg"
      title="Delete requirement?"
      submitText="Delete requirement"
      disableSubmitButton={
        isSubmitting || controlsLoading || !selectedRequirement
      }
    >
      <form
        id={ModalFormId.DELETE_FRAMEWORK_REQUIREMENT_CONFIRM}
        onSubmit={onSubmit}
      >
        {selectedRequirement?.requirementName
          ? `"${selectedRequirement.requirementName}"`
          : 'This requirement'}{' '}
        will be permanently deleted and unlinked from any linked controls.
        <WarningList>
          <li>This action cannot be undone.</li>
          <li>
            The requirement will be removed from any controls it&apos;s
            currently linked to.
          </li>
        </WarningList>
      </form>
    </ModalForm>
  )
}
